'use client';

import React, { useEffect, useState } from 'react';

interface Vote {
  id: string;
  participantName: string;
  choice: 'boy' | 'girl';
  timestamp: string;
}

interface VoteBarsProps {
  boyVotes: number;
  girlVotes: number;
  totalVotes: number;
  boyPct: number;
  girlPct: number;
  votes: Vote[];
}

export default function VoteBars({
  boyVotes,
  girlVotes,
  totalVotes,
  boyPct,
  girlPct,
  votes,
}: VoteBarsProps) {
  const [animatedBoyPct, setAnimatedBoyPct] = useState(0);
  const [animatedGirlPct, setAnimatedGirlPct] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedBoyPct(boyPct);
      setAnimatedGirlPct(girlPct);
    }, 200);
    return () => clearTimeout(timer);
  }, [boyPct, girlPct]);

  // Timeline — last 6 votes
  const recentVotes = [...votes].reverse().slice(0, 6);

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow p-6">
      <h3 className="text-base font-700 text-foreground mb-5">Distribución de Votos</h3>

      {/* VS display */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 text-center">
          <div className="text-3xl font-800 text-blue-500 font-tabular">{boyVotes}</div>
          <div className="text-xs font-600 text-blue-400 mt-0.5">💙 Niño</div>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <span className="text-xs font-700 text-muted-foreground">VS</span>
          </div>
        </div>
        <div className="flex-1 text-center">
          <div className="text-3xl font-800 text-pink-500 font-tabular">{girlVotes}</div>
          <div className="text-xs font-600 text-pink-400 mt-0.5">🩷 Niña</div>
        </div>
      </div>

      {/* Combined bar */}
      <div className="mb-6">
        <div className="h-6 rounded-full overflow-hidden flex">
          <div
            className="bg-boy-gradient h-full reveal-bar flex items-center justify-end pr-2"
            style={{ width: `${animatedBoyPct}%` }}
          >
            {animatedBoyPct >= 15 && (
              <span className="text-xs font-700 text-white font-tabular">{boyPct}%</span>
            )}
          </div>
          <div
            className="bg-girl-gradient h-full reveal-bar flex items-center justify-start pl-2"
            style={{ width: `${animatedGirlPct}%` }}
          >
            {animatedGirlPct >= 15 && (
              <span className="text-xs font-700 text-white font-tabular">{girlPct}%</span>
            )}
          </div>
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-xs text-muted-foreground">💙 {boyPct}%</span>
          <span className="text-xs text-muted-foreground font-tabular">{totalVotes} votos totales</span>
          <span className="text-xs text-muted-foreground">🩷 {girlPct}%</span>
        </div>
      </div>

      {/* Individual bars */}
      <div className="space-y-3 mb-5">
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-600 text-blue-500">💙 Niño</span>
            <span className="text-sm font-700 text-blue-500 font-tabular">
              {boyVotes} votos · {boyPct}%
            </span>
          </div>
          <div className="h-3 rounded-full bg-blue-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-boy-gradient reveal-bar"
              style={{ width: `${animatedBoyPct}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-600 text-pink-500">🩷 Niña</span>
            <span className="text-sm font-700 text-pink-500 font-tabular">
              {girlVotes} votos · {girlPct}%
            </span>
          </div>
          <div className="h-3 rounded-full bg-pink-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-girl-gradient reveal-bar"
              style={{ width: `${animatedGirlPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Recent votes timeline */}
      {recentVotes.length > 0 && (
        <div>
          <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
            Últimos votos
          </h4>
          <div className="space-y-1.5">
            {recentVotes.map((vote) => (
              <div
                key={`timeline-${vote.id}`}
                className="flex items-center gap-2.5 py-1.5 px-2.5 rounded-lg hover:bg-muted/40 transition-colors"
              >
                <span className="text-sm">
                  {vote.choice === 'boy' ? '💙' : '🩷'}
                </span>
                <span className="text-sm font-500 text-foreground flex-1 truncate">
                  {vote.participantName}
                </span>
                <span
                  className={`text-xs font-600 px-2 py-0.5 rounded-full ${
                    vote.choice === 'boy' ?'bg-blue-100 text-blue-500' :'bg-pink-100 text-pink-500'
                  }`}
                >
                  {vote.choice === 'boy' ? 'Niño' : 'Niña'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}